import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import projectsData from '../data/projects.json';
import './Projects.css';

interface PlatformLink {
  status: string;
  link?: string;
}

interface Project {
  name: string;
  description: string;
  tagline?: string;
  category?: string;
  url: string;
  color: string;
  technologies: string[];
  status: string;
  featured?: boolean;
  year?: string;
  web?: PlatformLink;
  googlePlay?: PlatformLink;
  appStore?: PlatformLink;
  privacy?: string;
}

const Projects: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const allProjects: Project[] = projectsData as Project[];

  const featuredProjects = allProjects.filter(p => p.featured);
  const otherProjects = allProjects.filter(p => !p.featured);

  return (
    <div className="projects-page">
      <div className="projects-hero">
        <div className="projects-hero-background">
          <div className="projects-hero-particles"></div>
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="gradient-orb gradient-orb-3"></div>
        </div>
        <div className="container">
          <motion.h1 
            className="projects-title"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Work
          </motion.h1>
          <motion.p 
            className="projects-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Platforms and web apps in active development and testing
          </motion.p>
        </div>
      </div>

      <div className="projects-content">
        <div className="container">
          {featuredProjects.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="projects-section-title">Current Projects</h2>
              <p className="projects-section-subtitle">In development and testing — not yet publicly launched</p>
              
              <motion.div 
                className="projects-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                {featuredProjects.map((project, index) => (
                  <ProjectCard key={index} project={project} variants={cardVariants} />
                ))}
              </motion.div>
            </motion.div>
          )}

          {otherProjects.length > 0 && (
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8 }}
              style={{ marginTop: '4rem' }}
            >
              <h2 className="projects-section-title">Earlier Work</h2>
              <p className="projects-section-subtitle">Projects from our journey over the years</p>
              
              <motion.div 
                className="projects-grid"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                {otherProjects.map((project, index) => (
                  <ProjectCard key={index} project={project} variants={cardVariants} />
                ))}
              </motion.div>
            </motion.div>
          )}

          <motion.div 
            className="projects-cta"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
          >
            <h2>Interested in working together?</h2>
            <p>Let's discuss your next project.</p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/contact" className="cta-button">
                Get in Touch
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

interface ProjectCardProps {
  project: Project;
  variants: any;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, variants }) => {
  return (
    <motion.div
      className={`project-card ${project.featured ? 'featured' : ''}`}
      variants={variants}
      whileHover={{ y: -12, scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <div className="project-header">
        <div className="project-status" style={{ backgroundColor: project.color }}>
          {project.status}
        </div>
        <div className="project-meta">
          {project.category && <span className="project-category">{project.category}</span>}
          {project.year && <span className="project-year">{project.year}</span>}
        </div>
      </div>
      
      <div className="project-content">
        <h3 className="project-name">{project.name}</h3>
        {project.tagline && <p className="project-tagline">{project.tagline}</p>}
        <p className="project-description">{project.description}</p>
      </div>
      
      <div className="project-footer">
        {(project.googlePlay ?? project.appStore) && (
          <div className="platform-links">
            {project.googlePlay && (
              project.googlePlay.link && project.googlePlay.link !== '#' ? (
                <a href={project.googlePlay.link} target="_blank" rel="noopener noreferrer" className="store-button" title="Get it on Google Play">
                  <img src="/GooglePlay.png" alt="Get it on Google Play" className="store-button-img" />
                </a>
              ) : (
                <span className="platform-link-plain"><span className="platform-label">Android</span> {project.googlePlay.status}</span>
              )
            )}
            {project.appStore && (
              project.appStore.link && project.appStore.link !== '#' ? (
                <a href={project.appStore.link} target="_blank" rel="noopener noreferrer" className="store-button" title="Download on the App Store">
                  <img src="/AppStore.png" alt="Download on the App Store" className="store-button-img" />
                </a>
              ) : (
                <span className="platform-link-plain"><span className="platform-label">iOS</span> {project.appStore.status}</span>
              )
            )}
          </div>
        )}
        {project.privacy && (
          <Link to={project.privacy} className="project-link">
            <span>Privacy policy</span>
          </Link>
        )}
        {project.url !== '#' ? (
          <a 
            href={project.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="project-link"
          >
            <span>View Project</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </a>
        ) : (
          <span className="project-link-disabled">Coming Soon</span>
        )}
      </div>
    </motion.div>
  );
};

export default Projects;
